import { BalanceAccount, calcRealtimeBalanceInfo, currencyTokens, EnterpriseTokenUsage, hundredthsToTokens } from '@run-diffusion/shared';

/**
 * Derives the display buckets for the token composition meter from a balance account.
 *
 * The paid balance is one number; buckets are an attribution. To stay honest with the renewal
 * ledger (computeRenewalRolloverPlan in shared/src/balances.ts), we use the SAME attribution the
 * renewal math uses: purchased-this-cycle tokens (P) are held first (spent last), then last
 * cycle's rolled-over top-up (R), and whatever remains is attributed to the monthly allotment.
 * That way the "Rolled over top-up" the meter shows is exactly what the ledger would expire at
 * the next renewal.
 *
 * All values are hundredths (0.01 tokens), matching BalanceAccount.
 */
export interface TokenBuckets {
	rolledOverTopUp: number // Last cycle's purchase remnant — expires at next renewal. Hide row when 0.
	monthly: number // Attributed to the current cycle's allotment (may exceed allotment for legacy surplus)
	topUp: number // Purchased this cycle — can roll over into one future cycle
	total: number // Realtime paid tokens (sum of the above)
}

export const deriveTokenBuckets = (balanceAccount: BalanceAccount): TokenBuckets => {
	if (!balanceAccount) {
		return { rolledOverTopUp: 0, monthly: 0, topUp: 0, total: 0 };
	}
	const { realtimeRunnitTokens } = calcRealtimeBalanceInfo(balanceAccount);
	const total: number = Math.max(realtimeRunnitTokens || 0, 0);

	const purchased: number = Math.max(balanceAccount.purchasedRunnitTokensThisCycle || 0, 0);
	const rolled: number = Math.max(balanceAccount.rolloverRunnitTokens || 0, 0);

	const topUp: number = Math.min(purchased, total);
	const rolledOverTopUp: number = Math.min(rolled, total - topUp);
	const monthly: number = total - topUp - rolledOverTopUp;

	return { rolledOverTopUp, monthly, topUp, total };
};

/**
 * The Monthly legend row's note on what the next renewal does to an account's monthly tokens.
 *
 * Renewal (computeRenewalRolloverPlan, shared by personal plans and non-enterprise team plans) tops
 * the monthly tokens up to the plan amount and never takes any away: it awards allotment - monthly,
 * floored at 0, carries this cycle's top-up forward and expires last cycle's. So an account already
 * holding the plan amount gets nothing. A bare "refills Oct 12" hid that, and "refills to 60,000"
 * beside 127,170 monthly tokens would read as if some were about to be taken away.
 *
 * Returns '' when there is no plan amount, or no scheduled renewal, to describe.
 */
export const deriveMonthlyRefillNote = (params: {
	monthly: number // Hundredths: the Monthly bucket from deriveTokenBuckets
	allotment: number // Hundredths: what the plan refills to, or a team plan resets to
	dateDisplay: string // The renewal date as shown, e.g. 'Oct 12'; '' when none is scheduled
	isTeamPlan: boolean
}): string => {
	const { monthly, allotment, dateDisplay, isTeamPlan } = params;
	if (!(allotment > 0) || !dateDisplay) return '';
	const amount: string = currencyTokens(hundredthsToTokens(allotment));
	if (monthly < allotment) return `${isTeamPlan ? 'resets' : 'refills'} to ${amount} on ${dateDisplay}`;
	return `already at or above its ${amount} ${isTeamPlan ? 'reset' : 'refill'}, nothing added ${dateDisplay}`;
};

export type TokenStatus = 'ok' | 'low' | 'depleted';

/**
 * Status drives the pill, the sidebar ring color, and the number color.
 * - depleted: nothing left — the paid balance has fallen to (or below) the daily-fallback level,
 *   or hit zero against a real allotment
 * - low: at or under 15% of the cycle allotment
 *
 * The ZERO and low rules require a known allotment. Callers derive their inputs from store data that
 * hydrates asynchronously, so "0 tokens against a 0 allotment" means "we don't know yet", not "you
 * are out" — reporting that as depleted flashes an alarming empty state on every refresh.
 *
 * A NEGATIVE balance is the exception and is checked first: only an enterprise team that overdrew
 * can be below zero, and an unloaded store reads 0, never less. So it needs no allotment to be
 * meaningful — a team sitting millions over budget must read depleted even with no budget set.
 * It is judged on the OVERDRAW as displayed (calcEnterpriseTokenUsage.isOver does the same), not on
 * the floored balance: hundredthsToTokens floors toward minus infinity, so half a token over read
 * as -1 here while the "used" number beside it, floored the other way, said 0.
 */
export const deriveTokenStatus = (params: {
	paidTokens: number
	cycleAllotment: number
	isOnDailyFallback: boolean
}): TokenStatus => {
	const { paidTokens, cycleAllotment, isOnDailyFallback } = params;
	if (isOnDailyFallback) return 'depleted';
	if (hundredthsToTokens(Math.max(-paidTokens, 0)) > 0) return 'depleted';
	/*
	Judged on the whole tokens the balance is DISPLAYED as, through the same converter the display uses
	(hundredthsToTokens floors). Compared in raw hundredths, 0.70 tokens counted as "some left" and came
	out 'low' — a yellow "0" — when the number beside it said there was nothing.
	 */
	const shownTokens: number = hundredthsToTokens(paidTokens);
	if (!(cycleAllotment > 0)) return 'ok';
	if (shownTokens <= 0) return 'depleted';
	if (paidTokens <= cycleAllotment * 0.15) return 'low';
	return 'ok';
};

export const TOKEN_STATUS_COLOR: Record<TokenStatus, string> = {
	ok: '#9968df',
	low: '#ffb347',
	depleted: '#ff5c7a',
};

/**
 * Amendment 1: the large dialog balance numeral is status-colored too — but its "ok" color is
 * white (amethyst is reserved for the sidebar ring).
 */
export const TOKEN_STATUS_HERO_COLOR: Record<TokenStatus, string> = {
	ok: '#fff',
	low: '#ffb347',
	depleted: '#ff5c7a',
};

export interface StatusPill {
	text: string
	kind: 'pill-warn' | 'pill-danger'
}

/**
 * The badge beside a consumer account's balance in the tokens dialog. It follows the status that
 * colours the number, so a red number gets a red badge, a yellow one a yellow badge, and a number
 * still loading (which reads ok) gets none.
 *
 * - low: RUNNING LOW
 * - depleted and the number reads 0: OUT OF TOKENS
 * - depleted with tokens still showing: USING DAILY TOKENS. Only the daily fallback is depleted while
 *   holding anything, so that is the one state this can be.
 *
 * "Out" used to have no badge of its own. Only the fallback could produce a red one, and a Free plan
 * never falls back (the daily tokens are its plan), so a Free account at 0 sat red and unexplained.
 * Out also beats the fallback: beside a 0, "using daily tokens" claims there are some left to use.
 *
 * shownTokens is the whole-token figure the number displays, not raw hundredths, so a sliver of a
 * token that reads "0" counts as out.
 */
export const deriveConsumerStatusPill = (params: {
	status: TokenStatus
	shownTokens: number
	isOnDailyFallback: boolean
}): StatusPill | null => {
	const { status, shownTokens, isOnDailyFallback } = params;
	if (status === 'low') return { text: 'RUNNING LOW', kind: 'pill-warn' };
	if (status !== 'depleted') return null;
	if (shownTokens <= 0) return { text: 'OUT OF TOKENS', kind: 'pill-danger' };
	if (isOnDailyFallback) return { text: 'USING DAILY TOKENS', kind: 'pill-danger' };
	return null;
};

/*
- progress: the account earns daily tokens, so the footer shows its allowance against the cap and
  links to the rewards page to earn more.
- claim: a Free plan with no allowance, so the footer points it at claiming the free plan.
 */
export type DailyRewardsFooter = 'progress' | 'claim';

/**
 * Which daily-rewards footer the tokens dialog shows, or null for none.
 *
 * Only accounts living on the daily allowance get one (a Free plan, or a paid plan that has spent
 * down to its daily tokens): a funded plan never spends them. A Free plan earns daily tokens only
 * once it has been claimed on the rewards page, which is also the only way off a spent sign-up
 * trial without paying. Hiding the footer from it, as the redesign first did, left new Free accounts
 * with no path to daily tokens at all. Paid plans always have an allowance, so "claim" is only ever
 * a Free plan.
 *
 * None while the balance account is loading: until it lands, even a claimed Free plan reads as
 * having no allowance, and the claim prompt would flash at people who already claimed.
 */
export const deriveDailyRewardsFooter = (params: {
	onDailyAllowance: boolean
	hasDailyAllowance: boolean
	isFreemium: boolean
	balanceLoading: boolean
}): DailyRewardsFooter | null => {
	const { onDailyAllowance, hasDailyAllowance, isFreemium, balanceLoading } = params;
	if (balanceLoading || !onDailyAllowance) return null;
	if (hasDailyAllowance) return 'progress';
	return isFreemium ? 'claim' : null;
};

/**
 * Sidebar ring fill, clamped to [0, 1]. Reads the same direction as the dialog's composition bar
 * for each layout, so the two never disagree at a glance.
 *
 * Consumer accounts (paid, freemium, and the daily fallback) count DOWN: the ring shows what is
 * still HELD, so it starts full on a fresh cycle and empties as tokens are spent. The denominator
 * is max(held, allotment) — the same trick the bar uses — so a balance sitting above the allotment
 * (rollover + top-ups) reads full instead of lying with >100%.
 *
 * Enterprise teams are the inverse and count UP: they spend against a fixed monthly budget, so the
 * ring starts empty at the reset and fills to the budget, matching that layout's "N of M used".
 * Going over budget (held < 0) pins it full rather than wrapping — whatever the budget. A budget-0
 * "Can Exceed" team has no ratio to draw, but millions over must not read as an empty ring beside a
 * red number saying millions used; the overdraw is judged in whole tokens like deriveTokenStatus.
 */
export const deriveRingFill = (params: {
	cycleAllotment: number
	held: number // Tokens still held: the consumer balance, or the team's remaining budget
	countUp?: boolean // Enterprise: fill grows with consumption instead of draining
}): number => {
	const { cycleAllotment, held, countUp } = params;
	if (countUp && hundredthsToTokens(Math.max(-held, 0)) > 0) return 1;
	if (!(cycleAllotment > 0)) return 0;
	// Floored to whole tokens like the number beside it, so a balance that reads "0" draws no arc. A
	// sliver of a token would otherwise paint a round-capped dot at 12 o'clock on a depleted (red) ring.
	const remaining: number = Math.max(hundredthsToTokens(held) * 100, 0);
	if (countUp) {
		const used: number = Math.max(cycleAllotment - remaining, 0);
		return Math.min(used / cycleAllotment, 1);
	}
	return Math.min(remaining / Math.max(remaining, cycleAllotment), 1);
};

export interface EnterpriseRing {
	fill: number
	status: TokenStatus
}

/**
 * An enterprise team's ring, from its shared usage figures (calcEnterpriseTokenUsage). Over budget
 * or paused is "gone": red and full, whatever the budget. Below that it fills toward the frozen
 * basis, and the 15% "low" threshold keys off that same basis so a mid-cycle budget edit can't
 * move it under the team.
 *
 * TokenRingMixin draws it for the active team; the company teams table draws one per row from that
 * row's own account. One rule here, so the same team gets the same ring wherever it is shown.
 */
export const deriveEnterpriseRing = (usage: EnterpriseTokenUsage): EnterpriseRing => ({
	fill: deriveRingFill({ cycleAllotment: usage.basis, held: usage.remaining, countUp: true }),
	status: (usage.isOver || usage.isPausedOut)
		? 'depleted'
		: deriveTokenStatus({ paidTokens: usage.remaining, cycleAllotment: usage.basis, isOnDailyFallback: false }),
});

export interface EnterpriseUsageCaption {
	text: string
	danger: boolean
}

/**
 * The line under an enterprise team's "tokens used" on the team dashboard: where this cycle's
 * spending leaves the team.
 *
 * It measures against the cycle basis (calcEnterpriseTokenUsage), which is the monthly budget only
 * while nothing has moved it. A transfer, a surplus kept at renewal, or a budget edit aimed at the
 * next reset all leave the basis somewhere else, and calling it the "monthly budget" there
 * contradicted the budget card beside it ("of 300,000 monthly budget" beside a 240,000 budget), so
 * it reads "this cycle" instead.
 *
 * A team with a budget and nothing funded yet says so. With no basis to measure against, it used to
 * read "No monthly budget set" beside a card showing its budget.
 *
 * Debt a reset's company bank could not cover is still owed, so it stays in "Over budget by", but it
 * is last cycle's. The caption says so, or a team that has spent nothing this cycle would read as
 * over budget with no explanation beside its "0 used".
 *
 * budget is the team's live monthlyRunnitTokensBudget, in hundredths.
 */
export const deriveEnterpriseUsageCaption = (params: {
	usage: EnterpriseTokenUsage
	budget: number
	teamLabel: string // displayLabels.team
}): EnterpriseUsageCaption => {
	const { usage, budget, teamLabel } = params;
	const tokens = (value: number): string => currencyTokens(hundredthsToTokens(value));
	const { basis, remaining, overage, carriedOverage, isOver, isPausedOut } = usage;
	const carriedTokens: number = hundredthsToTokens(carriedOverage);
	let carriedNote: string = '';
	if (carriedTokens > 0) {
		carriedNote = carriedTokens === hundredthsToTokens(overage)
			? ', carried over from last cycle'
			: `, ${tokens(carriedOverage)} of it carried over from last cycle`;
	}
	if (isPausedOut && isOver) return { text: `Over budget by ${tokens(overage)}${carriedNote}, generations paused`, danger: true };
	if (isPausedOut) return { text: 'Budget exhausted, generations paused until the next monthly reset', danger: true };
	if (isOver) return { text: `Over budget by ${tokens(overage)}${carriedNote}`, danger: true };
	// A sub-token overdraw is not "over", but it is not a remaining balance either: clamp it
	const held: string = tokens(Math.max(remaining, 0));
	if (basis > 0) return { text: `${held} remaining of ${tokens(basis)} ${basis === budget ? 'monthly budget' : 'this cycle'}`, danger: false };
	if (budget > 0) {
		return remaining > 0
			? { text: `${held} remaining this cycle`, danger: false }
			: { text: `${tokens(budget)} monthly budget, nothing funded yet this cycle`, danger: false };
	}
	if (remaining > 0) return { text: `${held} remaining, no monthly budget set`, danger: false };
	return { text: `No monthly budget set for this ${teamLabel}`, danger: false };
};

export interface EnterpriseUsageSplit {
	withinBudget: number // Whole tokens used within what the cycle made available
	overThisCycle: number // Whole tokens this cycle spent past it
	carried: number // Whole tokens of overage the cycle opened with, from a reset that could not cover it
}

/**
 * How the tokens dialog's legend splits an enterprise team's figures, in the whole tokens the rows
 * display, so withinBudget and overThisCycle add up to exactly the "N used" above them.
 *
 * Debt a reset's company bank could not cover is overage but not this cycle's usage, so it gets its
 * own row instead of joining either. Listed as plain "Over budget" beside "0 used", it read as if the
 * team had spent last month's overdraw this month.
 */
export const splitEnterpriseUsage = (usage: EnterpriseTokenUsage): EnterpriseUsageSplit => {
	const used: number = hundredthsToTokens(usage.used);
	const overage: number = usage.isOver ? hundredthsToTokens(usage.overage) : 0;
	const carried: number = Math.min(hundredthsToTokens(usage.carriedOverage), overage);
	const overThisCycle: number = Math.min(overage - carried, used);
	return { withinBudget: used - overThisCycle, overThisCycle, carried };
};
